import React, { useState, useRef, useEffect } from 'react';
import { chatWithTutor, generateTutorVisual } from '../services/geminiService';
import { 
  Send, User, Bot, Loader2, Sparkles, HeartPulse, 
  Trash2, Maximize2, ThumbsUp, ThumbsDown, 
  Check, X, Download, Zap, BookOpen, Stethoscope, Terminal, 
  ChevronRight, Info, ShieldCheck, Activity, Brain, Shield, 
  UserCircle, Volume2, VolumeX, Copy, Award, Heart, HelpCircle, 
  GraduationCap
} from 'lucide-react';
import { GamificationService } from '../services/gamificationService';
import { StorageService } from '../services/storageService';
import { useAuth } from '../components/AuthBridge';
import { AdUnit } from '../components/AdUnit';
import { ModuleHeader } from '../components/ModuleHeader';
import { useThemeTokens } from '../hooks/useActiveTheme';


interface Message {
  id: string;
  role: 'user' | 'model';
  text: string;
  timestamp: number;
  imageUrl?: string;
  isVisualizing?: boolean;
}

interface ResidentProfile {
  id: string;
  name: string;
  age: number;
  avatar: string;
  condition: string;
  description: string;
  prompt: string;
  vulnerability: string;
}

const RESIDENT_PROFILES: ResidentProfile[] = [
  {
    id: 'higgins',
    name: 'Mrs. Evelyn Higgins',
    age: 84,
    avatar: '👵',
    condition: 'Mild Dementia, Anxious',
    description: 'Fidgeting with her bedsheets, anxious about her daughter visiting later.',
    prompt: 'Evelyn Higgins, an anxious 84-year-old resident with mild dementia. You are worried because your daughter is late for her weekly visit.',
    vulnerability: 'Requires reassurance, gentle validation, and distraction techniques.'
  },
  {
    id: 'miller',
    name: 'Mr. Arthur Miller',
    age: 78,
    avatar: '👴',
    condition: 'Hip Fracture Rehab',
    description: 'Sitting in wheelchair, frustrated about his slow pacing and attempting to stand up unassisted.',
    prompt: 'Arthur Miller, a quick-tempered 78-year-old recovering from hip surgery. You are frustrated that you have to wait for help and want to prove you can walk.',
    vulnerability: 'Safety-critical. Must address fall risk, maintain client dignity, and offer a safe walker transfer protocol.'
  },
  {
    id: 'chen',
    name: 'Miss Grace Chen',
    age: 92,
    avatar: '👩‍🦳',
    condition: 'Low Vision, Diabetic Care',
    description: 'Quiet and polite, but feels embarrassed about needing assistance feeding herself.',
    prompt: 'Grace Chen, a polite but quiet 92-year-old resident with poor eyesight in diabetic care. You are feeling embarrassed about needing help with dinner.',
    vulnerability: 'Requires sensory enhancement, structured clock-placement explanation of the meal, and active independence facilitation.'
  }
];

const QUICK_TOPICS = [
  { 
    label: 'Sterile PPE Sequence', 
    icon: ShieldCheck, 
    intro: 'Walk me through donning/doffing order.',
    prompt: 'What is the absolute correct sequence for donning and doffing Personal Protective Equipment (PPE) according to standard board clinical guidelines? Please highlight common critical errors.' 
  },
  { 
    label: 'Board Failure Traps', 
    icon: Zap, 
    intro: 'Identify safety auto-fails.',
    prompt: 'What are the top 5 clinical "instant-fail" safety violations checked during the CNA physical skill evaluation? Provide tips on how to avoid each.' 
  },
  { 
    label: 'Fluid Conversion Math', 
    icon: Stethoscope, 
    intro: 'Practice fluid intake calculation.',
    prompt: 'Help me practice nursing intake and output fluid conversion. Give me a scenario involving ounces, cc, and mL, then explain the math step-by-step.' 
  },
  { 
    label: 'Resident Autonomy Rights', 
    icon: Heart, 
    intro: 'Master resident dignity ethics.',
    prompt: 'Can you give me a case study about promoting resident dignity, privacy, and active decision-making during sensitive care, then explain the rationale?' 
  }
];

export const AITutor: React.FC = () => {
  const { user, activeCert } = useAuth();
  const tokens = useThemeTokens();
  const [messages, setMessages] = useState<Message[]>([]);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRoleplay, setIsRoleplay] = useState(false);
  const [activeResidentId, setActiveResidentId] = useState<string>('higgins');
  const [ratings, setRatings] = useState<Record<string, 'up' | 'down'>>({});
  const [showRatingToast, setShowRatingToast] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [speakingMsgId, setSpeakingMsgId] = useState<string | null>(null);
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isPro = user?.planTier === 'paid';
  const points = GamificationService.getPoints ? GamificationService.getPoints() : 120; // fallback logic visual

  // Set default greeting or load saved chat sessions
  useEffect(() => {
    if (user?.id) {
        const progress = StorageService.getProgress(user.id);
        if (progress.tutorSession?.messages?.length > 0) {
            setMessages(progress.tutorSession.messages);
            setRatings(progress.tutorSession.ratings || {});
            
            // Reconstruct simulation states if saved
            const wasRoleplay = progress.tutorSession.messages.some((m: any) => m.text.includes("Resident Room") || m.text.includes("Evelyn") || m.text.includes("Arthur") || m.text.includes("Grace"));
            if (wasRoleplay) {
              setIsRoleplay(true);
            }
        } else {
            resetToModeGreeting(isRoleplay, activeResidentId);
        }
    }
  }, [user?.id, user?.state]);

  // Save session to localStorage lazily during conversation updates
  useEffect(() => {
    if (user?.id && messages.length > 0) {
        const timer = setTimeout(() => {
            StorageService.saveProgress(user.id || '', {
                tutorSession: { messages, ratings }
            });
            setIsSaved(true);
            const clearSavedTimer = setTimeout(() => setIsSaved(false), 2000);
            return () => clearTimeout(clearSavedTimer);
        }, 1000);
        return () => clearTimeout(timer);
    }
  }, [messages, ratings, user?.id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages, isLoading]);

  // Handle TTS Speak Aloud using browser native SpeechSynthesis
  const handleSpeak = (msgId: string, text: string) => {
    if ('speechSynthesis' in window) {
      if (speakingMsgId === msgId) {
        window.speechSynthesis.cancel();
        setSpeakingMsgId(null);
        return;
      }
      window.speechSynthesis.cancel();
      const cleanText = text.replace(/[*#`_]/g, '');
      const utterance = new SpeechSynthesisUtterance(cleanText);
      
      utterance.onend = () => {
        setSpeakingMsgId(null);
      };
      utterance.onerror = () => {
        setSpeakingMsgId(null);
      };

      // Set realistic educational vocal pitches
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      
      setSpeakingMsgId(msgId);
      window.speechSynthesis.speak(utterance);
    }
  };

  // Safe release speech on exit
  useEffect(() => {
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const handleCopyText = (msgId: string, text: string) => {
    const cleanText = text.replace(/[*#`_]/g, '');
    navigator.clipboard.writeText(cleanText).then(() => {
      setCopiedMsgId(msgId);
      setTimeout(() => setCopiedMsgId(null), 1500);
    });
  };

  const resetToModeGreeting = (roleplay: boolean, resId: string) => {
    if (roleplay) {
      const resident = RESIDENT_PROFILES.find(r => r.id === resId) || RESIDENT_PROFILES[0];
      setMessages([{
        id: 'welcome_resident',
        role: 'model',
        text: `🚪 [Entered Room: ${resident.name}] ${resident.avatar} \n"${resident.description}"\n\nHow do you respond? Maintain proper clinical etiquette, make eye contact, introduce yourself, and remember critical safety standards.`,
        timestamp: Date.now()
      }]);
    } else {
      setMessages([{ 
          id: 'welcome_instructor',
          role: 'model', 
          text: `🎓 Clinical Study Coach Online. Developed by certified nursing educators & calibrated to ${user?.state || 'Global'} Board Standards. \n\nI can explain complex nursing procedures, analyze nursing scenarios peer-reviewed by RNs, test you on medical terminology, or review high-risk clinical rationales. Select a bento study option on the left or type your inquiry below!`,
          timestamp: Date.now()
      }]);
    }
  };

  const handleToggleMode = (roleplay: boolean) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setSpeakingMsgId(null);
    setIsRoleplay(roleplay);
    resetToModeGreeting(roleplay, activeResidentId);
    if (window.trackEvent) window.trackEvent('tutor_mode_toggled', 'ai_tutor', roleplay ? 'resident' : 'instructor');
  };

  const handleResidentSelect = (resId: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setSpeakingMsgId(null);
    setActiveResidentId(resId);
    resetToModeGreeting(true, resId);
    if (window.trackEvent) window.trackEvent('tutor_resident_selected', 'ai_tutor', resId);
  };

  const handleSend = async (userText: string) => {
    if (!userText.trim() || isLoading) return;

    const userMsgId = `u_${Date.now()}`;
    const cleanUserText = userText.trim();
    
    setMessages(prev => [...prev, { 
        id: userMsgId, 
        role: 'user', 
        text: cleanUserText,
        timestamp: Date.now()
    }]);
    setIsLoading(true);
    setInput('');

    if (window.trackEvent) window.trackEvent('tutor_message_sent', 'ai_tutor', cleanUserText.slice(0, 50));

    // Construct the context, feeding character profiles to simulate real roleplay
    let promptDecorator = cleanUserText;
    if (isRoleplay) {
      const resident = RESIDENT_PROFILES.find(r => r.id === activeResidentId) || RESIDENT_PROFILES[0];
      promptDecorator = `[CRITICAL ROLEPLAY INSTRUCTION: You are roleplaying as ${resident.prompt}. Respond directly as this patient in character using brief, natural speech. Do not lecture. Do not use instructor markdown formulas.] USER MESSAGE: ${cleanUserText}`;
    }

    const historyContext = messages.slice(-10).map(m => ({
        role: m.role,
        parts: [{ text: m.text }]
    }));

    try {
        const response = await chatWithTutor(
          user?.id || '', 
          historyContext, 
          promptDecorator, 
          user?.planTier, 
          isRoleplay ? 'resident' : 'instructor'
        );
        
        let responseText = response;
        if (isRoleplay && response.includes("USER MESSAGE:")) {
          responseText = response.split("USER MESSAGE:").pop() || response;
        }

        setMessages(prev => [...prev, { 
            id: `m_${Date.now()}`,
            role: 'model', 
            text: responseText || "Instructor Logic Stream paused. Please prompt again.",
            timestamp: Date.now()
        }]);
    } catch (err) {
        setMessages(prev => [...prev, { 
            id: `err_${Date.now()}`,
            role: 'model', 
            text: "Network logic sync error. Check active data bridge.",
            timestamp: Date.now()
        }]);
    } finally {
        setIsLoading(false);
    }
  };

  const handleVisualize = async (msgId: string) => {
    const msg = messages.find(m => m.id === msgId);
    if (!msg || msg.imageUrl || msg.isVisualizing) return;

    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, isVisualizing: true } : m));
    
    if (window.trackEvent) window.trackEvent('tutor_visualize_requested', 'ai_tutor', msg.text.slice(0, 50));

    try {
        const imageUrl = await generateTutorVisual(msg.text.slice(0, 80), msg.text);
        if (imageUrl) {
            setMessages(prev => prev.map(m => m.id === msgId ? { ...m, imageUrl, isVisualizing: false } : m));
            GamificationService.addPoints(15);
        } else {
            throw new Error("Empty image");
        }
    } catch (e) {
        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, imageUrl: undefined, isVisualizing: false } : m));
    }
  };

  const handleRate = async (msgId: string, type: 'up' | 'down') => {
    setRatings(prev => {
      const next = { ...prev };
      if (next[msgId] === type) delete next[msgId];
      else next[msgId] = type;
      return next;
    });
    setShowRatingToast(true);
    setTimeout(() => setShowRatingToast(false), 2000);
    if (window.trackEvent) window.trackEvent('tutor_rating_submitted', 'ai_tutor', type);
  };

  const clearChat = () => {
    if (confirm('Purge tutor simulated logic history?')) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      setSpeakingMsgId(null);
      resetToModeGreeting(isRoleplay, activeResidentId);
      setRatings({});
      if (window.trackEvent) window.trackEvent('tutor_chat_cleared', 'ai_tutor');
    }
  };

  const exportChat = () => {
    const header = `CNA ELITE STUDY REGISTER - AI CLINICAL INTERACTIVE LOG\nDate: ${new Date().toLocaleDateString()}\nMode: ${isRoleplay ? 'Therapeutic resident Simulation' : 'State standards instructor Mode'}\n\n`;
    const text = messages.map(m => `[${m.role === 'user' ? 'STUDENT' : 'AI SYSTEM'}] ${m.text}`).join('\n\n');
    const blob = new Blob([header + text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CNA_StudyLog_Session_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    if (window.trackEvent) window.trackEvent('tutor_chat_exported', 'ai_tutor');
  };

  const selectedResident = RESIDENT_PROFILES.find(r => r.id === activeResidentId) || RESIDENT_PROFILES[0];

  return (
    <div className="max-w-6xl mx-auto pb-24 text-slate-900 dark:text-slate-100 animate-fade-in">
      <AdUnit placement="ai_tutor" />
      
      <ModuleHeader 
        icon={Brain}
        title="AI Study Helper"
        description="Ask questions about lessons or practice talking directly to simulated patient characters to build your confidence."
        badge="Study Assistant"
        color="indigo"
      />

      {/* Expanded Illustration Viewer */}
      {expandedImage && (
        <div className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-xl flex flex-col items-center justify-center p-4 md:p-12 animate-fade-in no-print" onClick={() => setExpandedImage(null)}>
            <div className="absolute top-4 right-4 flex items-center gap-3">
              <a href={expandedImage} download="Clinical_Illustration.png" onClick={e => e.stopPropagation()} className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all" title="Download Print Quality">
                <Download size={22}/>
              </a>
              <button className="p-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-all"><X size={22}/></button>
            </div>
            <img src={expandedImage} className="max-w-full max-h-[80vh] rounded-3xl shadow-2xl border border-white/20 object-contain animate-scale-up" alt="Clinical Aid Expanded" />
            <p className="text-white/60 text-xs font-semibold tracking-widest mt-4 uppercase">AI Educational Render Diagram • Clinical Reference Schema</p>
        </div>
      )}

      {/* Split-Screen Interactive Layout Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6 font-bold">
        
        {/* Left column sidebar toggler, bento tools, active study parameters */}
        <div className="lg:col-span-4 space-y-6">
          
          {/* Active Mode Controller Card */}
          <div className="bg-white dark:bg-slate-900 p-5 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-xl relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-indigo-500/10 dark:bg-indigo-500/20 rounded-full blur-xl"></div>
            
            <h3 className="text-[10px] tracking-widest font-black uppercase text-indigo-500 dark:text-indigo-400 mb-3 flex items-center gap-1.5">
              <Activity size={12} className="text-indigo-500 dark:text-indigo-400" /> Mode Selector Status
            </h3>
            
            <div className="flex bg-slate-100 dark:bg-slate-950 p-1.5 rounded-2xl gap-1">
              <button 
                onClick={() => handleToggleMode(false)}
                className={`flex-1 py-3 px-1 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all cursor-pointer ${!isRoleplay ? 'bg-indigo-600 text-white shadow-xl' : 'text-slate-400 dark:text-slate-500 hover:text-slate-900'}`}
              >
                🎓 Academic Prep
              </button>
              <button 
                onClick={() => handleToggleMode(true)}
                className={`flex-1 py-3 px-1 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all cursor-pointer ${isRoleplay ? 'bg-teal-600 text-white shadow-xl' : 'text-slate-400 dark:text-slate-500 hover:text-slate-900'}`}
              >
                🛋️ Bedside Sim
              </button>
            </div>

            {/* Dynamic context descriptions */}
            <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-950/55 rounded-xl text-[10px] space-y-1.5 border border-slate-100 dark:border-slate-850">
              <div className="flex justify-between items-center border-b border-slate-200/50 dark:border-slate-800 pb-1.5">
                <span className="font-extrabold text-slate-400 uppercase tracking-wider">Active Channel</span>
                <span className={`px-2.5 py-0.5 rounded-md font-black uppercase text-[8px] text-white ${isRoleplay ? 'bg-teal-500' : 'bg-indigo-500'}`}>
                  {isRoleplay ? 'Bedside Roleplay' : 'Theory Master'}
                </span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 font-bold leading-normal">
                {isRoleplay 
                  ? "Interact directly with simulated patient profiles. Practice bedside conversation limits, emotional support boundaries, and fall prevention protocols."
                  : `Ask direct board exam concepts. Fully calibrated to the ${user?.state || 'Global'} standards using dynamic logic kernels.`
                }
              </p>
            </div>
          </div>

          {/* Resident Simulated Rooms List (when roleplay active) */}
          {isRoleplay ? (
            <div className="bg-gradient-to-br from-teal-50 to-teal-150/20 dark:from-slate-900 dark:to-slate-950 p-5 rounded-[2rem] border border-teal-100 dark:border-slate-800 shadow-xl space-y-3 relative overflow-hidden animate-slide-right">
              <div className="absolute top-0 right-0 w-24 h-24 bg-teal-500/10 rounded-full blur-xl"></div>
              
              <div className="flex items-center justify-between border-b border-teal-100/45 dark:border-slate-800 pb-2 mb-1">
                <h3 className="text-[10px] tracking-widest font-black uppercase text-teal-600 dark:text-teal-400 flex items-center gap-1.5">
                  👨‍⚕️ Select Resident Room
                </h3>
                <span className="text-[8px] font-black uppercase bg-teal-200/40 dark:bg-teal-900/35 text-teal-700 dark:text-teal-400 px-2 py-0.5 rounded-full">3 Active</span>
              </div>
              
              <div className="space-y-2.5">
                {RESIDENT_PROFILES.map(res => {
                  let riskColor = "bg-amber-400";
                  let riskLabel = "Low Bed Risk";
                  if (res.id === 'miller') { riskColor = "bg-rose-500 animate-pulse"; riskLabel = "Critical Fall Risk"; }
                  else if (res.id === 'higgins') { riskColor = "bg-amber-500 animate-pulse"; riskLabel = "Moderate Anxiety"; }
                  else if (res.id === 'chen') { riskColor = "bg-teal-500 animate-pulse"; riskLabel = "Sensory Support"; }

                  return (
                    <button
                      key={res.id}
                      onClick={() => handleResidentSelect(res.id)}
                      className={`w-full p-4 rounded-3xl border text-left flex items-start gap-3.5 transition-all outline-none cursor-pointer group relative ${activeResidentId === res.id ? 'bg-teal-600 text-white border-teal-700 shadow-xl scale-[1.01]' : 'bg-white hover:bg-teal-50/20 dark:bg-slate-900 dark:hover:bg-slate-850 dark:border-slate-800 text-slate-850 dark:text-slate-300'}`}
                    >
                      <span className="text-3xl mt-1 shrink-0 block group-hover:scale-105 transition-transform">{res.avatar}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1">
                          <p className={`text-xs font-black truncate  ${activeResidentId === res.id ? 'text-white' : 'text-slate-900 dark:text-white'}`}>{res.name}</p>
                          <span className={`text-[7px] font-black uppercase px-2 py-0.5 rounded-full shrink-0 ${activeResidentId === res.id ? 'bg-teal-850 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>Age {res.age}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${riskColor}`}></span>
                          <p className={`text-[8px] font-black uppercase tracking-wider ${activeResidentId === res.id ? 'text-teal-200' : 'text-slate-450 dark:text-slate-500'}`}>{riskLabel}</p>
                        </div>
                        <p className={`text-[9px] mt-1.5 line-clamp-1 leading-normal font-bold ${activeResidentId === res.id ? 'text-teal-100' : 'text-slate-500 dark:text-slate-400'}`}>{res.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Patient vulnerabilities guidance tips */}
              <div className="bg-teal-600/10 rounded-xl p-3 border border-teal-555/20 mt-3 text-[9px] text-teal-750 dark:text-teal-300 leading-normal font-bold">
                <span className="font-black uppercase block mb-1">💡 Simulation Focus Action Strategy</span>
                <p className="font-bold leading-normal">{selectedResident.vulnerability}</p>
              </div>
            </div>
          ) : (
            // Bento Quick Topics
            <div className="bg-white dark:bg-slate-900 p-5 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
              <h3 className="text-[10px] tracking-widest font-black uppercase text-indigo-500 dark:text-indigo-400 flex items-center gap-1.5">
                📚 Bento Quick Study Chips
              </h3>
              
              <div className="grid grid-cols-1 gap-2.5">
                {QUICK_TOPICS.map((topic, i) => {
                  const Icon = topic.icon;
                  return (
                    <button 
                      key={i}
                      onClick={() => handleSend(topic.prompt)}
                      className="p-3.5 bg-slate-50 hover:bg-indigo-50 dark:bg-slate-950 dark:hover:bg-indigo-950/20 rounded-2xl border border-slate-100 dark:border-slate-850 hover:border-indigo-200 dark:hover:border-indigo-950 text-left transition-all flex items-center gap-3.5 active:scale-95 cursor-pointer group"
                    >
                      <div className="p-2.5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-850 text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 rounded-xl shrink-0 transition-colors shadow-sm">
                        <Icon size={16} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-black truncate text-slate-800 dark:text-white">{topic.label}</h4>
                        <p className="text-[9px] text-slate-400 dark:text-slate-500 font-bold line-clamp-1 truncate mt-0.5">"{topic.intro}"</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Status parameters indicator sync */}
              <div className="pt-3 border-t border-slate-150 dark:border-slate-800 flex items-center justify-between text-[10px]">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="font-extrabold text-slate-450 uppercase tracking-widest text-[8px]">Tutor Logic Sync</span>
                </div>
                <span className="font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-widest text-[8px]">{points} Studio Points Accrued</span>
              </div>
            </div>
          )}

        </div>

        {/* Right column main chat conversation container */}
        <div className="lg:col-span-8">
          
          <div className="bg-white dark:bg-slate-900 rounded-[2.2rem] shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col h-[calc(110vh-23rem)] min-h-[550px] relative">
            
            {/* Conversations Context header */}
            <div className={`p-4 md:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0 transition-colors duration-500 ${isRoleplay ? 'bg-teal-50/50 dark:bg-teal-950/20' : 'bg-slate-50/50 dark:bg-slate-950/20'}`}>
              <div className="flex items-center gap-3.5 min-w-0">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-md ${isRoleplay ? 'bg-teal-600 text-white' : 'bg-indigo-600 text-white'}`}>
                  {isRoleplay ? <UserCircle size={22} className="animate-pulse" /> : <Bot size={22} />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-black text-slate-900 dark:text-white leading-none">
                      {isRoleplay ? selectedResident.name : 'Senior Board Instructor'}
                    </h3>
                    {isPro && (
                      <span className="bg-amber-400/10 text-amber-600 dark:text-amber-400 text-[7px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-amber-400/20 shadow-sm flex items-center gap-1 shrink-0">
                        <Shield size={8} /> Pro
                      </span>
                    )}
                  </div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-1 truncate">
                    {isRoleplay ? `Simulated Bedside Room • ${selectedResident.condition}` : `Calibration: ${user?.state || 'Global'} Board Standards Connected`}
                  </p>
                </div>
              </div>

              {/* Utility session controls */}
              <div className="flex items-center gap-1 shrink-0">
                <button 
                  onClick={exportChat} 
                  className="p-2.5 text-slate-400 dark:text-slate-500 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-800 rounded-xl transition-all" 
                  title="Export Transcript"
                >
                  <Download size={18} />
                </button>
                <button 
                  onClick={clearChat} 
                  className="p-2.5 text-slate-400 dark:text-slate-500 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-xl transition-all" 
                  title="Reset Discussion"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            {/* Main Chat Canvas with message threads */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6 bg-slate-50/40 dark:bg-slate-950/30 custom-scrollbar relative">
              <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #4f46e5 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
              
              {messages.map((msg, idx) => {
                const isUser = msg.role === 'user';
                const isThisSpeaking = speakingMsgId === msg.id;
                const isThisCopied = copiedMsgId === msg.id;
                
                return (
                  <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-slide-up`}>
                    <div className={`flex max-w-[92%] md:max-w-[85%] gap-2.5 md:gap-3.5 ${isUser ? 'flex-row-reverse' : ''}`}>
                      
                      {/* Avatar */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 shadow-sm border ${isUser ? 'bg-slate-900 border-slate-950 text-white' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-indigo-500 dark:text-indigo-400'}`}>
                        {isUser ? <User size={14} /> : (isRoleplay ? <span className="text-sm block leading-none">{selectedResident.avatar}</span> : <Bot size={14} />)}
                      </div>

                      {/* Bubble Details */}
                      <div className={`space-y-1.5 ${isUser ? 'items-end' : 'items-start'} min-w-0`}>
                        <div className={`p-4 md:p-5 rounded-[1.3rem] text-xs md:text-sm leading-relaxed font-bold shadow-sm relative ${isUser ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-850 text-slate-850 dark:text-slate-200 rounded-tl-none'}`}>
                          {/* Colored decorative bar for AI bubbles */}
                          {!isUser && (
                            <div className={`absolute left-0 top-3 bottom-3 w-1.5 rounded-r-md ${isRoleplay ? 'bg-teal-500' : 'bg-indigo-500'}`}></div>
                          )}

                          <p className="whitespace-pre-wrap break-words leading-relaxed pl-1">{msg.text}</p>

                          {/* Visual Aid Diagram Box inside response */}
                          {(msg.imageUrl || msg.isVisualizing) && (
                            <div className="mt-4 pt-3.5 border-t border-slate-100 dark:border-slate-805">
                              {msg.isVisualizing ? (
                                <div className="aspect-video w-full bg-slate-50 dark:bg-slate-950 rounded-xl flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 dark:border-slate-800 animate-pulse">
                                  <Loader2 className={`${tokens.accentText} animate-spin`} size={18} />
                                  <span className="text-[8px] font-black tracking-widest text-slate-400 dark:text-slate-500 uppercase">Visualizing Diagnostic Schema...</span>
                                </div>
                              ) : msg.imageUrl && (
                                <div className="group relative rounded-xl overflow-hidden shadow-md border-4 border-slate-50 dark:border-slate-800 bg-slate-100 dark:bg-slate-950 animate-pop">
                                    <img src={msg.imageUrl} className="w-full h-auto object-cover max-h-[220px]" alt="Visual Schema Asset" />
                                    <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                                        <button onClick={() => setExpandedImage(msg.imageUrl || null)} className="p-3 bg-white text-slate-950 rounded-full shadow-2xl hover:scale-110 transition-transform">
                                            <Maximize2 size={16} />
                                        </button>
                                    </div>
                                    <div className="absolute top-2 left-2 bg-slate-950/80 backdrop-blur-md px-2.5 py-1 text-[7px] text-white font-black tracking-widest uppercase rounded">
                                      AI Model Graphic
                                    </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Interactive Speech & Utility Footer Actions (only model messages) */}
                        {!isUser && idx !== 0 && (
                          <div className="flex items-center gap-2 px-1">
                            {/* Speak Aloud trigger button */}
                            <button
                              onClick={() => handleSpeak(msg.id, msg.text)}
                              className={`p-1.5 border border-slate-100 dark:border-slate-800 hover:border-indigo-150 dark:hover:border-indigo-950 hover:bg-indigo-50/50 dark:hover:bg-slate-805 rounded-lg flex items-center gap-1 transition-all ${isThisSpeaking ? 'bg-indigo-50 text-indigo-600 border-indigo-200 animate-pulse' : 'text-slate-400 dark:text-slate-500'}`}
                              title={isThisSpeaking ? 'Stop voice' : 'Listen voice'}
                            >
                              {isThisSpeaking ? <VolumeX size={12} /> : <Volume2 size={12} />}
                              <span className="text-[7px] font-black tracking-wider uppercase">{isThisSpeaking ? 'Mute' : 'Speak'}</span>
                            </button>

                            {/* Copy button */}
                            <button
                              onClick={() => handleCopyText(msg.id, msg.text)}
                              className="p-1.5 border border-slate-145 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-805 rounded-lg text-slate-400 dark:text-slate-500 flex items-center gap-1 transition-all active:scale-95"
                              title="Copy transcription"
                            >
                              {isThisCopied ? <Check className="text-emerald-500" size={12} /> : <Copy size={12} />}
                              <span className="text-[7px] font-black tracking-wider uppercase">{isThisCopied ? 'Copied' : 'Copy'}</span>
                            </button>

                            {/* Up & Down ratings button */}
                            <div className="flex items-center border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-lg p-0.5">
                              <button onClick={() => handleRate(msg.id, 'up')} className={`p-1 rounded transition-all ${ratings[msg.id] === 'up' ? 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/20' : 'text-slate-300 dark:text-slate-650 hover:text-slate-450'}`}><ThumbsUp size={11}/></button>
                              <button onClick={() => handleRate(msg.id, 'down')} className={`p-1 rounded transition-all ${ratings[msg.id] === 'down' ? 'text-rose-500 bg-rose-50 dark:bg-rose-950/20' : 'text-slate-300 dark:text-slate-650 hover:text-slate-450'}`}><ThumbsDown size={11}/></button>
                            </div>

                            {/* Inline Visual Aid builder (if image not active) */}
                            {!msg.imageUrl && !msg.isVisualizing && !isRoleplay && (
                              <button
                                onClick={() => handleVisualize(msg.id)}
                                className="px-2.5 py-1.5 bg-indigo-50/50 hover:bg-indigo-50 dark:bg-slate-850 dark:hover:bg-slate-800 text-indigo-600 dark:text-indigo-400 font-black text-[8px] uppercase tracking-wider rounded-lg border border-indigo-100/50 dark:border-slate-800 flex items-center gap-1 shadow-sm transition-all"
                              >
                                <Sparkles size={10} /> Diagram
                              </button>
                            )}
                          </div>
                        )}

                      </div>

                    </div>
                  </div>
                );
              })}

              {isLoading && (
                <div className="flex justify-start ml-11">
                  <div className="px-4 py-3 bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-850 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-2">
                    <div className="flex gap-1 shrink-0">
                      <div className={`w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:-0.3s] ${isRoleplay ? 'bg-teal-500' : 'bg-indigo-500'}`}></div>
                      <div className={`w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:-0.15s] ${isRoleplay ? 'bg-teal-500' : 'bg-indigo-500'}`}></div>
                      <div className={`w-1.5 h-1.5 rounded-full animate-bounce ${isRoleplay ? 'bg-teal-500' : 'bg-indigo-500'}`}></div>
                    </div>
                    <span className="text-[8px] md:text-[9px] font-black text-slate-400 tracking-wider uppercase">
                      {isRoleplay ? `${selectedResident.name} responding...` : 'Instructor formulating rationale...'}
                    </span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Module Block */}
            <div className="p-4 md:p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 shrink-0">
              <form 
                onSubmit={e => { e.preventDefault(); handleSend(input); }}
                className="relative max-w-4xl mx-auto flex items-center gap-2"
              >
                <div className="relative flex-1 group">
                  <Terminal className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 dark:text-slate-600 group-focus-within:text-indigo-500 transition-colors" size={16} />
                  <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder={isRoleplay ? `Enter bed simulated response to ${selectedResident.name}...` : "Submit clinical query (e.g., 'explain perineal standards')..."}
                    className="w-full pl-11 pr-4 py-4 md:py-4.5 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-850 rounded-2xl outline-none focus:border-indigo-400 dark:focus:border-indigo-950 focus:bg-white dark:focus:bg-slate-950 transition-all font-bold text-xs md:text-sm text-slate-850 dark:text-slate-200"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className={`px-6 py-4 rounded-2xl text-white font-black text-[10px] uppercase tracking-widest disabled:opacity-30 active:scale-95 transition-all flex items-center gap-1.5 shrink-0 border-b-2 select-none h-[49px] ${isRoleplay ? 'bg-teal-600 hover:bg-teal-700 border-teal-800' : 'bg-indigo-600 hover:bg-indigo-700 border-indigo-800'}`}
                >
                  <Send size={12} /> Send
                </button>
              </form>

              <div className="flex items-center justify-center gap-1.5 text-center mt-3 text-[8px] text-slate-400 font-extrabold tracking-widest uppercase">
                <Info size={10} />
                <span>
                  {isRoleplay 
                    ? 'Clinical Scenario Simulation. Prompts support professional therapeutic validation standards.' 
                    : 'Board Exam study models are formulated and peer-reviewed by expert nurse educators.'
                  }
                </span>
              </div>
            </div>

          </div>

        </div>

      </div>

      {showRatingToast && (
        <div className="my-toast fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] bg-slate-950/90 backdrop-blur text-white border border-white/15 rounded-full px-5 py-2.5 flex items-center gap-2 shadow-2xl animate-pop">
          <CheckCircle2 size={16} className="text-emerald-400" />
          <span className="text-[9px] tracking-widest uppercase font-black">AI Calibration Index Sync Completed</span>
        </div>
      )}
    </div>
  );
};
